import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { fmtRelative, fmtBytes } from "@/lib/format";
import { Activity, HardDrive, MessageSquare, GitBranch, ShieldCheck, FileImage, UserPlus, ListChecks, Pencil } from "lucide-react";

const ACTION_ICONS = {
  comment_added: MessageSquare,
  client_comment_added: MessageSquare,
  version_uploaded: GitBranch,
  review_item_uploaded: FileImage,
  approval_approved: ShieldCheck,
  approval_changes_requested: Pencil,
  member_invited: UserPlus,
  task_created: ListChecks,
};

const ACTION_LABELS = {
  comment_added: "commented on",
  client_comment_added: "added a client comment on",
  version_uploaded: "uploaded a new version of",
  review_item_uploaded: "uploaded",
  approval_approved: "approved",
  approval_changes_requested: "requested changes on",
  member_invited: "invited",
  task_created: "created task",
};

export default function ActivityFeed() {
  const { activeWorkspaceId } = useAuth();
  const [items, setItems] = useState([]);
  const [storage, setStorage] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    const [a, s] = await Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/activity`),
      api.get(`/workspaces/${activeWorkspaceId}/storage`),
    ]);
    setItems(a.data); setStorage(s.data);
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const total = storage?.total_bytes || 0;
  const types = storage?.by_type || {};
  const sortBytes = (arr) => [...(arr || [])].sort((a, b) => b.bytes - a.bytes).slice(0, 8);

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Activity & storage"
        description="Live event stream plus a breakdown of how storage is being used."
        testid="activity-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 border border-border" data-testid="activity-list">
          <div className="label-eyebrow px-4 py-3 border-b border-border bg-muted/40">Recent activity</div>
          {items.length === 0 && <div className="px-4 py-8 text-sm text-muted-foreground">No activity yet.</div>}
          <ul>
            {items.map((it) => {
              const Icon = ACTION_ICONS[it.action] || Activity;
              const label = ACTION_LABELS[it.action] || it.action.replace(/_/g, " ");
              return (
                <li key={it.id} className="px-4 py-3 border-b border-border flex items-start gap-3 text-sm" data-testid={`activity-row-${it.id}`}>
                  <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">
                      <span className="font-medium">{it.actor_name}</span>{" "}
                      <span className="text-muted-foreground">{label}</span>{" "}
                      <span className="font-medium">{it.target_label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{fmtRelative(it.created_at)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="space-y-3" data-testid="storage-breakdown">
          <div className="border border-border p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">Storage used</div>
              <HardDrive className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="heading-display text-3xl mb-3">{fmtBytes(total)}</div>
            <div className="text-xs text-muted-foreground">{storage?.item_count || 0} review items</div>
          </div>
          <div className="border border-border p-5">
            <div className="label-eyebrow mb-3">By type</div>
            {Object.entries(types).map(([k, v]) => (
              <div key={k} className="mb-2 last:mb-0" data-testid={`storage-type-${k}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-mono">{k}</span>
                  <span className="text-muted-foreground">{fmtBytes(v)}</span>
                </div>
                <div className="h-1 bg-muted">
                  <div className="h-full bg-primary" style={{ width: `${total ? (v / total) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
          <Breakdown title="By client" items={sortBytes(storage?.by_client)} total={total} testid="storage-by-client" />
          <Breakdown title="By project" items={sortBytes(storage?.by_project)} total={total} testid="storage-by-project" />
          <Breakdown title="By user" items={sortBytes(storage?.by_user)} total={total} testid="storage-by-user" />
        </div>
      </div>
    </>
  );
}

function Breakdown({ title, items, total, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border border-border p-5" data-testid={testid}>
      <div className="label-eyebrow mb-3">{title}</div>
      {items.map((it, i) => (
        <div key={i} className="mb-2 last:mb-0">
          <div className="flex justify-between text-xs mb-1">
            <span className="truncate font-medium">{it.name}{it.client_name ? <span className="text-muted-foreground ml-1">· {it.client_name}</span> : ""}</span>
            <span className="text-muted-foreground shrink-0 ml-2">{fmtBytes(it.bytes)}</span>
          </div>
          <div className="h-1 bg-muted">
            <div className="h-full bg-foreground" style={{ width: `${total ? (it.bytes / total) * 100 : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
